import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Shield, 
  AlertTriangle, 
  Database, 
  Zap,
  TrendingUp,
  Users
} from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { loggingApiService, AnalyticsData, SystemHealth } from '../../../../services/loggingApiService';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<any>;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  status?: 'healthy' | 'warning' | 'error';
  subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  status = 'healthy',
  subtitle 
}) => {

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${
            status === 'healthy' ? 'bg-green-100 dark:bg-green-800/50' :
            status === 'warning' ? 'bg-yellow-100 dark:bg-yellow-800/50' : 'bg-red-100 dark:bg-red-800/50'
          }`}>
            <Icon className={`w-6 h-6 ${
              status === 'healthy' ? 'text-green-600 dark:text-green-400' :
              status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
            }`} />
          </div>
          <div>
            <h3 className="text-sm font-medium">{title}</h3>
            {subtitle && (
              <p className="text-xs opacity-75">{subtitle}</p>
            )}
          </div>
        </div>
        {trend && (
          <div className={`flex items-center space-x-1 text-xs ${
            trend.isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            <TrendingUp className={`w-3 h-3 ${trend.isPositive ? '' : 'rotate-180'}`} />
            <span>{Math.abs(trend.value)}%</span>
          </div>
        )}
      </div>
      
      <div className="mt-4">
        <div className="text-3xl font-bold text-gray-900 dark:text-white">{value}</div>
      </div>
    </div>
  );
};

/**
 * Logging Overview Panel Component
 * 
 * Displays real-time system metrics, health status, and activity overview
 */
export const LoggingOverviewPanel: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [period, setPeriod] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [analyticsRes, healthRes] = await Promise.all([
          loggingApiService.getAnalytics(period),
          loggingApiService.getSystemHealth()
        ]);

        setAnalytics(analyticsRes);
        setSystemHealth(healthRes);
      } catch (error) {
        console.error('Failed to fetch logging overview data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [period]);

  if (loading && !analytics) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
          <span className="ml-3 text-gray-600">Loading system metrics...</span>
        </div>
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-red-600 font-medium">Failed to load logging data</p>
            <p className="text-gray-500 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            System Overview
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Real-time logging system metrics and health status
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Period:</span>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as typeof period)}
            className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* System Health Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">
            System Health
          </h4>
          <Badge 
            variant={systemHealth?.status === 'healthy' ? 'default' : 'destructive'}
            className="flex items-center space-x-1"
          >
            <div className={`w-2 h-2 rounded-full ${
              systemHealth?.status === 'healthy' ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>{systemHealth?.status || 'Unknown'}</span>
          </Badge>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Database Health */}
          <div className="text-center">
            <div className="text-2xl font-bold text-lex-accent mb-2">
              <Database className="w-8 h-8 mx-auto" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Database
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {systemHealth?.database.connected ? 'Connected' : 'Disconnected'}
            </div>
            {systemHealth?.database.lastActivity && (
              <div className="text-xs text-gray-400 mt-1">
                Last Activity: {new Date(systemHealth.database.lastActivity).toLocaleTimeString()}
              </div>
            )}
          </div>
          
          {/* Queue Status */}
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-2">
              <Zap className="w-8 h-8 mx-auto" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Queue Processing
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Active Jobs: {
                systemHealth?.queues ? 
                  Object.values(systemHealth.queues).reduce((sum, queue) => sum + queue.active, 0) : 
                  0
              }
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Real-time Processing
            </div>
          </div>
          
          {/* Active Alerts */}
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 mb-2">
              <AlertTriangle className="w-8 h-8 mx-auto" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Security Alerts
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Active: {systemHealth?.alerts.activeCount || 0}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Require Attention
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Activities"
          value={analytics?.totalActivities?.toLocaleString() || '0'}
          icon={Activity}
          subtitle={`Last ${period}`}
          status="healthy"
        />
        
        <MetricCard
          title="Active Users"
          value={analytics?.uniqueActiveUsers || '0'}
          icon={Users}
          subtitle="Unique users active"
          status="healthy"
        />
        
        <MetricCard
          title="High Risk Activities"
          value={analytics?.highRiskCount || '0'}
          icon={Shield}
          subtitle="Risk score ≥ 70"
          status={analytics && analytics.highRiskCount > 0 ? 'warning' : 'healthy'}
        />
        
        <MetricCard
          title="Security Events"
          value={analytics?.securityMetrics.failedAttempts || '0'}
          icon={AlertTriangle}
          subtitle="Failed attempts"
          status={analytics && analytics.securityMetrics.failedAttempts > 0 ? 'warning' : 'healthy'}
        />
      </div>

      {/* Activity Distribution */}
      {analytics && analytics.activityCounts.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Activity Distribution
          </h4>
          
          <div className="space-y-3">
            {analytics.activityCounts.slice(0, 5).map((activity) => {
              const percentage = (activity.count / analytics.totalActivities) * 100;
              
              return (
                <div key={activity.type} className="flex items-center">
                  <div className="w-32 text-sm text-gray-600 dark:text-gray-400 truncate">
                    {activity.type.replace(/_/g, ' ')}
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-lex-accent h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-20 text-sm text-gray-900 dark:text-white text-right">
                    {activity.count.toLocaleString()}
                  </div>
                  <div className="w-12 text-xs text-gray-500 text-right">
                    {percentage.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Security Metrics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Security Metrics
        </h4>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {analytics?.securityMetrics.failedAttempts || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Failed Attempts</div>
            <div className="text-xs text-gray-500 mt-1">4xx/5xx errors</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {analytics?.securityMetrics.afterHoursActivity || 0}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">After Hours</div>
            <div className="text-xs text-gray-500 mt-1">Outside 6am-10pm</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="text-2xl font-bold text-lex-accent">
              {analytics?.securityMetrics.averageRiskScore || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg Risk Score</div>
            <div className="text-xs text-gray-500 mt-1">Out of 100</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {analytics?.securityMetrics.uniqueLocations || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Access Locations</div>
            <div className="text-xs text-gray-500 mt-1">Unique IPs</div>
          </div>
        </div>
      </div>
    </div>
  );
}; 